import { useEffect, useMemo, useState } from "react";
import { FiPlus, FiX } from "react-icons/fi";
import AdminCard from "../components/admin/AdminCard";
import AdminTable from "../components/admin/AdminTable";
import Modal from "../components/common/Modal";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import Loader from "../components/common/Loader";
import formatCurrency from "../utils/formatCurrency";
import { isEmpty } from "../utils/validators";
import * as serviceService from "../services/serviceService";

const initialFormState = {
  id: null,
  serviceName: "",
  serviceType: "",
  hennaType: "",
  servicePrice: "",
  serviceDurationMinutes: "",
  serviceDescription: "",
  isActive: true,
  desiredFileNames: [""],
  files: [],
  existingImages: [],
  removeImageIds: [],
};

const safeCallGetAllServices = async () => {
  if (typeof serviceService.getAllServices === "function") {
    return serviceService.getAllServices();
  }

  if (typeof serviceService.getServices === "function") {
    return serviceService.getServices();
  }

  throw new Error("Service list method was not found in serviceService.");
};

const safeCallCreateService = async (formData) => {
  if (typeof serviceService.createService === "function") {
    return serviceService.createService(formData);
  }

  if (typeof serviceService.addService === "function") {
    return serviceService.addService(formData);
  }

  throw new Error("Create service method was not found in serviceService.");
};

const safeCallUpdateService = async (id, formData) => {
  if (typeof serviceService.updateService === "function") {
    return serviceService.updateService(id, formData);
  }

  if (typeof serviceService.editService === "function") {
    return serviceService.editService(id, formData);
  }

  throw new Error("Update service method was not found in serviceService.");
};

const safeCallDeleteService = async (id) => {
  if (typeof serviceService.deleteService === "function") {
    return serviceService.deleteService(id);
  }

  if (typeof serviceService.removeService === "function") {
    return serviceService.removeService(id);
  }

  throw new Error("Delete service method was not found in serviceService.");
};

const normalizeServiceImages = (service) => {
  const images = Array.isArray(service?.serviceImages) ? service.serviceImages : [];
  if (images.length > 0) {
    return images;
  }

  return service?.serviceImageUrl
    ? [{ id: `fallback-${service.id}`, imageUrl: service.serviceImageUrl, displayOrder: 0 }]
    : [];
};

const ManageServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadServices = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await safeCallGetAllServices();
      const data = response?.data || response;

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Failed to load services.");
      }

      setServices(Array.isArray(data?.serviceList) ? data.serviceList : []);
    } catch (err) {
      setError(err?.message || "We could not load services.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServices();
  }, []);

  const filteredServices = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return services;

    return services.filter((service) => {
      return (
        service?.serviceName?.toLowerCase().includes(query) ||
        service?.serviceType?.toLowerCase().includes(query) ||
        service?.hennaType?.toLowerCase().includes(query)
      );
    });
  }, [services, search]);

  const resetMessages = () => {
    setError("");
    setSuccessMessage("");
  };

  const openCreateModal = () => {
    resetMessages();
    setForm(initialFormState);
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (service) => {
    resetMessages();

    const existingImages = normalizeServiceImages(service);

    setForm({
      id: service?.id ?? null,
      serviceName: service?.serviceName || "",
      serviceType: service?.serviceType || "",
      hennaType: service?.hennaType || "",
      servicePrice:
        service?.servicePrice !== undefined && service?.servicePrice !== null
          ? String(service.servicePrice)
          : "",
      serviceDurationMinutes:
        service?.serviceDurationMinutes !== undefined &&
        service?.serviceDurationMinutes !== null
          ? String(service.serviceDurationMinutes)
          : "",
      serviceDescription: service?.serviceDescription || "",
      isActive: service?.active !== false,
      desiredFileNames: [""],
      files: [],
      existingImages,
      removeImageIds: [],
    });

    setFormErrors({});
    setIsModalOpen(true);
  };

  const validateForm = () => {
    const nextErrors = {};
    const existingRemaining = form.existingImages.filter(
      (image) => !form.removeImageIds.includes(image.id)
    );

    if (isEmpty(form.serviceName)) {
      nextErrors.serviceName = "Please enter a service name.";
    }

    if (isEmpty(form.serviceType)) {
      nextErrors.serviceType = "Please enter a service type.";
    }

    if (isEmpty(form.hennaType)) {
      nextErrors.hennaType = "Please enter a henna type.";
    }

    if (isEmpty(form.servicePrice) || Number(form.servicePrice) < 0) {
      nextErrors.servicePrice = "Please enter a valid price.";
    }

    if (
      isEmpty(form.serviceDurationMinutes) ||
      Number(form.serviceDurationMinutes) <= 0
    ) {
      nextErrors.serviceDurationMinutes =
        "Please enter a valid duration in minutes.";
    }

    if (!form.id && form.files.length === 0) {
      nextErrors.files = "Please upload at least one image.";
    }

    if (form.id && existingRemaining.length === 0 && form.files.length === 0) {
      nextErrors.files = "A service must keep at least one image.";
    }

    return nextErrors;
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    resetMessages();

    if (type === "checkbox") {
      setForm((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleFilesChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);

    setForm((prev) => ({
      ...prev,
      files: selectedFiles,
      desiredFileNames:
        selectedFiles.length > 0
          ? selectedFiles.map((file) => file?.name || "")
          : [""],
    }));

    setFormErrors((prev) => ({
      ...prev,
      files: "",
    }));
  };

  const handleDesiredNameChange = (index, value) => {
    setForm((prev) => {
      const nextNames = [...prev.desiredFileNames];
      nextNames[index] = value;
      return {
        ...prev,
        desiredFileNames: nextNames,
      };
    });
  };

  const toggleRemoveExistingImage = (imageId) => {
    setForm((prev) => {
      const exists = prev.removeImageIds.includes(imageId);
      return {
        ...prev,
        removeImageIds: exists
          ? prev.removeImageIds.filter((id) => id !== imageId)
          : [...prev.removeImageIds, imageId],
      };
    });
  };

  const buildMultipartFormData = () => {
    const multipartData = new FormData();

    multipartData.append("serviceName", form.serviceName.trim());
    multipartData.append("serviceType", form.serviceType.trim());
    multipartData.append("hennaType", form.hennaType.trim());
    multipartData.append("servicePrice", String(Number(form.servicePrice)));
    multipartData.append(
      "serviceDurationMinutes",
      String(Number(form.serviceDurationMinutes))
    );
    multipartData.append("serviceDescription", form.serviceDescription.trim());
    multipartData.append("isActive", String(Boolean(form.isActive)));

    form.files.forEach((file) => {
      multipartData.append("files", file);
    });

    form.desiredFileNames.forEach((name) => {
      multipartData.append("desiredFileNames", name?.trim() || "");
    });

    form.removeImageIds.forEach((id) => {
      multipartData.append("removeImageIds", String(id));
    });

    return multipartData;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    resetMessages();

    const nextErrors = validateForm();
    setFormErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    try {
      setActionLoading(true);

      const multipartData = buildMultipartFormData();

      const response = form.id
        ? await safeCallUpdateService(form.id, multipartData)
        : await safeCallCreateService(multipartData);

      const data = response?.data || response;

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(
          data.message ||
            (form.id ? "Failed to update service." : "Failed to create service.")
        );
      }

      setSuccessMessage(
        data?.message ||
          (form.id
            ? "The service was updated successfully."
            : "The service was created successfully.")
      );

      setIsModalOpen(false);
      setForm(initialFormState);
      setFormErrors({});

      await loadServices();
    } catch (err) {
      setError(err?.message || "We could not save the service.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;

    try {
      setActionLoading(true);
      resetMessages();

      const response = await safeCallDeleteService(deleteTarget.id);
      const data = response?.data || response;

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Failed to delete service.");
      }

      setSuccessMessage(data?.message || "The service was deleted successfully.");
      setDeleteTarget(null);

      await loadServices();
    } catch (err) {
      setError(err?.message || "We could not delete the service.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {loading ? <Loader text="Loading service records..." /> : null}

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-700">{error}</p>
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
          <p className="text-sm font-medium text-green-700">{successMessage}</p>
        </div>
      ) : null}

      <AdminCard
        title="Manage Services"
        subtitle="Create, review, and update the services clients see when they book."
        actions={
          <div className="flex flex-wrap gap-3">
            <Input
              name="serviceSearch"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name, type, or henna style..."
              className="min-w-[240px]"
            />

            <Button
              type="button"
              variant="primary"
              leftIcon={<FiPlus />}
              onClick={openCreateModal}
            >
              Add Service
            </Button>
          </div>
        }
      >
        <div className="max-h-[520px] overflow-y-auto pr-1">
          <AdminTable
            loading={loading}
            data={filteredServices}
            emptyMessage="No services match the current search."
            columns={[
              {
                key: "serviceName",
                label: "Service Name",
                render: (row) => row?.serviceName || "—",
              },
              {
                key: "serviceType",
                label: "Type",
                render: (row) => row?.serviceType || "—",
              },
              {
                key: "hennaType",
                label: "Henna Type",
                render: (row) => row?.hennaType || "—",
              },
              {
                key: "servicePrice",
                label: "Base Price",
                render: (row) =>
                  row?.servicePrice !== undefined && row?.servicePrice !== null
                    ? formatCurrency(row.servicePrice)
                    : "—",
              },
              {
                key: "serviceDurationMinutes",
                label: "Duration",
                render: (row) =>
                  row?.serviceDurationMinutes
                    ? `${row.serviceDurationMinutes} min`
                    : "—",
              },
              {
                key: "imageCount",
                label: "Images",
                render: (row) => {
                  const images = normalizeServiceImages(row);
                  return images.length;
                },
              },
              {
                key: "active",
                label: "Status",
                render: (row) =>
                  row?.active !== false ? (
                    <span className="status-chip status-chip-confirmed">ACTIVE</span>
                  ) : (
                    <span className="status-chip status-chip-canceled">INACTIVE</span>
                  ),
              },
            ]}
            actions={(row) => [
              {
                label: "Edit",
                variant: "secondary",
                onClick: () => openEditModal(row),
              },
              {
                label: "Delete",
                variant: "danger",
                onClick: () => setDeleteTarget(row),
              },
            ]}
          />
        </div>
      </AdminCard>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          if (!actionLoading) {
            setIsModalOpen(false);
          }
        }}
        title={form.id ? "Edit Service" : "Add New Service"}
        size="lg"
        footer={
          <div className="flex flex-col gap-3 md:flex-row md:justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
              disabled={actionLoading}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              form="service-form"
              variant="primary"
              loading={actionLoading}
            >
              {form.id ? "Save Changes" : "Create Service"}
            </Button>
          </div>
        }
      >
        <form id="service-form" onSubmit={handleSubmit} className="space-y-5">
          <div className="form-grid two">
            <Input
              label="Service Name"
              name="serviceName"
              value={form.serviceName}
              onChange={handleChange}
              error={formErrors.serviceName}
              required
            />

            <Input
              label="Service Type"
              name="serviceType"
              value={form.serviceType}
              onChange={handleChange}
              error={formErrors.serviceType}
              required
            />
          </div>

          <div className="form-grid two">
            <Input
              label="Henna Type"
              name="hennaType"
              value={form.hennaType}
              onChange={handleChange}
              error={formErrors.hennaType}
              required
            />

            <Input
              label="Base Price"
              name="servicePrice"
              type="number"
              min="0"
              step="0.01"
              value={form.servicePrice}
              onChange={handleChange}
              error={formErrors.servicePrice}
              required
            />
          </div>

          <div className="form-grid two">
            <Input
              label="Duration (Minutes)"
              name="serviceDurationMinutes"
              type="number"
              min="1"
              step="1"
              value={form.serviceDurationMinutes}
              onChange={handleChange}
              error={formErrors.serviceDurationMinutes}
              required
            />

            <div>
              <label className="field-label" htmlFor="isActive">
                Visibility
              </label>
              <label className="flex min-h-[50px] items-center gap-3 rounded-xl border border-goldlight bg-white px-4 py-3">
                <input
                  id="isActive"
                  name="isActive"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={handleChange}
                />
                <span className="text-sm text-[#3b2f2f]">
                  Show this service to clients
                </span>
              </label>
            </div>
          </div>

          <div>
            <label className="field-label" htmlFor="serviceDescription">
              Service Description
            </label>
            <textarea
              id="serviceDescription"
              name="serviceDescription"
              rows={5}
              value={form.serviceDescription}
              onChange={handleChange}
              className="w-full rounded-xl border border-goldlight bg-white px-4 py-3 text-sm text-[#2f2626] outline-none transition focus:border-henna"
              placeholder="Describe this service, the style, and what clients can expect."
            />
          </div>

          <div className="space-y-4 rounded-2xl border border-goldlight bg-soft-cream/60 p-4">
            <div>
              <p className="text-sm font-semibold text-[#3b2f2f]">
                Service Image Gallery
              </p>
              <p className="mt-1 text-sm text-muted-warm">
                Upload multiple images. The first remaining image becomes the cover image shown first to clients.
              </p>
            </div>

            {form.existingImages.length > 0 ? (
              <div>
                <p className="mb-3 text-sm font-medium text-[#3b2f2f]">
                  Existing Images
                </p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {form.existingImages.map((image) => {
                    const markedForRemoval = form.removeImageIds.includes(image.id);

                    return (
                      <div
                        key={image.id}
                        className={`rounded-2xl border p-3 ${
                          markedForRemoval
                            ? "border-red-300 bg-red-50"
                            : "border-goldlight bg-white"
                        }`}
                      >
                        <div className="overflow-hidden rounded-xl bg-soft-cream">
                          <img
                            src={image.imageUrl}
                            alt="Service"
                            className="h-32 w-full object-cover"
                          />
                        </div>

                        <div className="mt-3 flex items-center justify-between gap-3">
                          <span className="text-xs text-muted-warm">
                            {markedForRemoval ? "Will be removed" : "Keep image"}
                          </span>

                          <button
                            type="button"
                            onClick={() => toggleRemoveExistingImage(image.id)}
                            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${
                              markedForRemoval
                                ? "bg-white text-red-700 border border-red-300"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            <FiX size={12} />
                            {markedForRemoval ? "Undo" : "Remove"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div>
              <label className="field-label" htmlFor="files">
                Upload New Images
              </label>
              <input
                id="files"
                name="files"
                type="file"
                accept="image/*"
                multiple
                onChange={handleFilesChange}
                className="w-full rounded-xl border border-goldlight bg-white px-4 py-3 text-sm text-[#2f2626]"
              />
              {formErrors.files ? (
                <p className="mt-2 text-sm text-red-600">{formErrors.files}</p>
              ) : null}
            </div>

            {form.files.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm font-medium text-[#3b2f2f]">
                  Optional File Names
                </p>

                {form.files.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="rounded-xl bg-white p-3 border border-goldlight">
                    <p className="text-sm font-medium text-[#3b2f2f]">
                      {file.name}
                    </p>
                    <Input
                      label="Stored File Name"
                      name={`desiredFileName-${index}`}
                      value={form.desiredFileNames[index] || ""}
                      onChange={(event) =>
                        handleDesiredNameChange(index, event.target.value)
                      }
                      placeholder="Leave as is or rename before upload"
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={() => {
          if (!actionLoading) setDeleteTarget(null);
        }}
        title="Delete Service"
        size="sm"
        footer={
          <div className="flex flex-col gap-3 md:flex-row md:justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
              disabled={actionLoading}
            >
              Cancel
            </Button>

            <Button
              type="button"
              variant="danger"
              loading={actionLoading}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm font-semibold text-[#3b2f2f]">
            {deleteTarget?.serviceName || "Selected service"}
          </p>

          <p className="text-sm leading-6 text-muted-warm">
            Are you sure you want to delete this service?
          </p>

          <p className="text-sm leading-6 text-muted-warm">
            This will also remove the related service images from the service record.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default ManageServices;